import React, { useEffect, useRef, useState } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	RiLayoutGridLine,
	RiFileList3Line,
	RiAuctionLine,
	RiWallet3Line,
	RiUserLine,
	RiChat3Line,
	RiNotification3Line,
	RiArrowLeftSLine,
	RiArrowRightSLine,
	RiAddLine,
	RiSettings3Line,
	RiShieldCheckLine,
	RiQuestionLine,
	RiLogoutBoxRLine,
	RiArrowDownSLine,
} from "@remixicon/react";
import { logout } from "../../store/authSlice";
import logo from "../../Assets/images/ordr-chartreuse-logo.svg";
import avatar from "../../Assets/images/no-profile-avatar.jpg";

// Same six sections as the mobile app's bottom tabs; each sub-page highlights its parent.
// Market updates lives in the left-hand sidebar rather than the header.
export const NAV = [
	{ to: "/", label: "Dashboard", icon: RiLayoutGridLine, end: true, match: ["/market-updates"] },
	{ to: "/orders", label: "Orders", icon: RiFileList3Line, match: ["/orders"] },
	{ to: "/bids", label: "Bids", icon: RiAuctionLine, match: ["/bids", "/closed-market"] },
	{ to: "/funds", label: "Funds", icon: RiWallet3Line, match: ["/funds"] },
	{
		to: "/controls",
		label: "Controls",
		icon: RiSettings3Line,
		match: ["/controls", "/settings", "/company-verification", "/locations", "/notifications", "/terms-of-service", "/privacy-policy"],
	},
	{ to: "/help", label: "Help", icon: RiQuestionLine, match: ["/help", "/messages"] },
];

const ACCOUNT_MENU = [
	{ to: "/settings", label: "Edit profile", icon: RiUserLine },
	{ to: "/company-verification", label: "Business verification", icon: RiShieldCheckLine },
	{ to: "/controls", label: "All controls", icon: RiSettings3Line },
];

export default function Header({ unread }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const user = useSelector((s) => s.auth.user);
	const [menuOpen, setMenuOpen] = useState(false);
	const menuRef = useRef(null);

	useEffect(() => {
		setMenuOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		const close = (e) => {
			if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
		};
		document.addEventListener("mousedown", close);
		return () => document.removeEventListener("mousedown", close);
	}, []);

	const handleLogout = () => {
		dispatch(logout());
		navigate("/login");
	};

	const fullName = user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email : "";
	const isActive = (item) => (item.match?.some((p) => location.pathname.startsWith(p)) ? true : undefined);

	return (
		<header className="app-header">
			<div className="header-brand">
				<Link to="/" className="header-logo">
					<img src={logo} alt="Ordr" />
					<div className="company-name">
						<span>Buyer</span>
						<span>Central</span>
					</div>
				</Link>
				<span className="brand-divider" aria-hidden="true" />
				<span className="nav-arrows">
					<button type="button" className="nav-arrow-btn" onClick={() => navigate(-1)} aria-label="Go back">
						<RiArrowLeftSLine size={18} />
					</button>
					<button type="button" className="nav-arrow-btn" onClick={() => navigate(1)} aria-label="Go forward">
						<RiArrowRightSLine size={18} />
					</button>
				</span>
			</div>

			{/* Phone app bar: the rest of the navigation lives in MobileTabBar */}
			<div className="header-mobile-actions">
				<NavLink to="/notifications" className="icon-btn" title="Notifications">
					<RiNotification3Line size={21} />
					{unread ? <span className="notif-dot" /> : null}
				</NavLink>
				<Link to="/settings" className="account-trigger" aria-label="Account settings">
					<img src={user?.profileImage || avatar} alt="" />
				</Link>
			</div>

			<div className="header-nav">
				<nav className="header-links">
					{NAV.map((item) => (
						<NavLink
							key={item.to}
							to={item.to}
							end={item.end}
							className={({ isActive: routeActive }) => `header-link${(isActive(item) ?? routeActive) ? " active" : ""}`}
						>
							<item.icon size={18} />
							<span>{item.label}</span>
						</NavLink>
					))}
				</nav>

				<div className="header-actions">
					<Link to="/bids/new" className="btn-accent-solid header-cta">
						<RiAddLine size={18} /> New bid request
					</Link>
					<NavLink to="/messages" className="icon-btn" title="Messages">
						<RiChat3Line size={19} />
						<span className="icon-btn-label">Messages</span>
					</NavLink>
					<NavLink to="/notifications" className="icon-btn" title="Notifications">
						<RiNotification3Line size={19} />
						<span className="icon-btn-label">Notifications</span>
						{unread ? <span className="notif-dot" aria-label={`${unread} unread notifications`} /> : null}
					</NavLink>

					<div className="account-menu" ref={menuRef}>
						<button type="button" className="account-trigger" onClick={() => setMenuOpen((o) => !o)} aria-expanded={menuOpen}>
							<img src={user?.profileImage || avatar} alt="" />
							<RiArrowDownSLine size={16} />
						</button>
						{menuOpen && (
							<div className="account-dropdown">
								<div className="account-head">
									<div className="fw-bold text-truncate">{fullName || "Your account"}</div>
									{user?.email ? <div className="text-muted small text-truncate">{user.email}</div> : null}
								</div>
								{ACCOUNT_MENU.map((item) => (
									<Link key={item.to} to={item.to} className="account-item">
										<item.icon size={17} /> {item.label}
									</Link>
								))}
								<button type="button" className="account-item logout" onClick={handleLogout}>
									<RiLogoutBoxRLine size={17} /> Log out
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</header>
	);
}
